import { useEffect, useState } from "react";
import axios from "axios";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const API = `${window.location.origin}/api`;
const GOLD = "#C4A671";
const PIE_COLORS = ["#C4A671", "#7A8FA6", "#9FBF8F", "#B76E79"];

const RANGES = [
  ["today", "Oggi"],
  ["7d", "Ultimi 7 Giorni"],
  ["month", "Questo Mese"],
  ["year", "Anno · Mese per Mese"],
];

const SECTION_LABELS = {
  collezione: "Collezione", filosofia: "Visione / Timeline", "terra-arsa": "Terra Arsa",
  scilla: "Scilla", blanche: "Blanche", claire: "Claire", artigianalita: "Artigianalità",
  "sommelier-club": "Sommelier Club", faq: "FAQ", contatti: "Contatti / Allocazione",
};

const fmtDur = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`);

function Card({ title, children, className = "", testid }) {
  return (
    <div className={`bg-[#111] border border-[#2a2a2a] p-5 ${className}`} data-testid={testid}>
      <div className="overline text-[9px] text-[#8B7A55] mb-4">{title}</div>
      {children}
    </div>
  );
}

export default function AnalyticsPanel({ authHeader }) {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clarityId, setClarityId] = useState("");
  const [claritySaved, setClaritySaved] = useState(false);

  const load = async (r) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/analytics/summary?range=${r}`, authHeader);
      setData(data);
    } catch (e) { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(range); }, [range]); // eslint-disable-line
  useEffect(() => {
    axios.get(`${API}/analytics/clarity-id`).then(({ data }) => setClarityId(data.project_id || ""));
  }, []);

  const saveClarity = async () => {
    await axios.post(`${API}/admin/analytics/clarity`, { project_id: clarityId }, authHeader);
    setClaritySaved(true);
    setTimeout(() => setClaritySaved(false), 2500);
  };

  const k = data?.kpi || {};
  const b2bBiz = (data?.b2b || []).filter((o) => o.is_business);
  const b2bIsp = (data?.b2b || []).filter((o) => !o.is_business);

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* Filtri temporali */}
      <div className="flex flex-wrap gap-2">
        {RANGES.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setRange(key)}
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.15em] border transition ${
              range === key
                ? "border-[#C4A671] text-[#C4A671] bg-[#C4A671]/10"
                : "border-[#2a2a2a] text-[#7A7568] hover:text-[#C9C3B5]"
            }`}
            data-testid={`analytics-range-${key}`}
          >
            {label}
          </button>
        ))}
        {loading && <span className="text-[#7A7568] text-xs self-center ml-2">Caricamento…</span>}
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          ["Visite", k.visits ?? "—", "analytics-kpi-visits"],
          ["Visitatori Unici", k.unique_sessions ?? "—", "analytics-kpi-uniques"],
          ["Tempo Medio Sessione", k.avg_duration_seconds != null ? fmtDur(k.avg_duration_seconds) : "—", "analytics-kpi-duration"],
          ["Da Smartphone", `${k.mobile_pct ?? 0}%`, "analytics-kpi-mobile"],
        ].map(([label, value, tid]) => (
          <div key={label} className="bg-[#111] border border-[#2a2a2a] p-5" data-testid={tid}>
            <div className="overline text-[9px] text-[#8B7A55]">{label}</div>
            <div className="font-serif-display italic text-3xl text-[#F2EFE8] mt-2">{value}</div>
          </div>
        ))}
      </div>

      {/* Grafico andamento */}
      <Card title="📈 Andamento Visite — picchi campagne LinkedIn/Social" testid="analytics-chart">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data?.series || []}>
              <defs>
                <linearGradient id="gold" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={GOLD} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" stroke="#555" tick={{ fontSize: 10 }} />
              <YAxis stroke="#555" tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#111", border: "1px solid #2a2a2a", fontSize: 12 }} />
              <Area type="monotone" dataKey="visits" stroke={GOLD} fill="url(#gold)" strokeWidth={2} name="Visite" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {(data?.series || []).length === 0 && !loading && (
          <p className="text-[#7A7568] text-xs mt-2">Nessuna visita registrata nel periodo — il tracking parte da ora.</p>
        )}
      </Card>

      {/* Device / Geo / Sorgenti */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="📱 Dispositivi" testid="analytics-devices">
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={(data?.devices || []).map((d) => ({ name: d.device === "mobile" ? "Smartphone" : d.device === "tablet" ? "Tablet" : "Desktop/PC", value: d.visits }))}
                  dataKey="value" innerRadius={45} outerRadius={70} paddingAngle={3}
                >
                  {(data?.devices || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 11, color: "#C9C3B5" }} />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #2a2a2a", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {(data?.devices || []).map((d) => (
            <div key={d.device} className="flex justify-between text-xs text-[#C9C3B5] py-1 border-t border-[#222]">
              <span className="capitalize">{d.device === "mobile" ? "📱 Smartphone" : d.device === "tablet" ? "📲 Tablet" : "💻 Desktop/PC"}</span>
              <span className="text-[#C4A671]">{d.visits}</span>
            </div>
          ))}
        </Card>

        <Card title="🌍 Provenienza — Paese / Città" testid="analytics-geo">
          <div className="max-h-72 overflow-y-auto space-y-1">
            {(data?.geo || []).map((g, i) => (
              <div key={i} className="flex justify-between text-xs text-[#C9C3B5] py-1.5 border-b border-[#222]">
                <span>{g.country} · <span className="text-[#7A7568]">{g.city}</span></span>
                <span className="text-[#C4A671]">{g.visits}</span>
              </div>
            ))}
            {(data?.geo || []).length === 0 && <p className="text-[#7A7568] text-xs">In attesa di dati…</p>}
          </div>
        </Card>

        <Card title="🔗 Sorgenti di Traffico" testid="analytics-sources">
          <div className="max-h-72 overflow-y-auto space-y-1">
            {(data?.sources || []).map((s, i) => (
              <div key={i} className="flex justify-between text-xs text-[#C9C3B5] py-1.5 border-b border-[#222]">
                <span>{s.source}</span>
                <span className="text-[#C4A671]">{s.visits}</span>
              </div>
            ))}
            {(data?.sources || []).length === 0 && <p className="text-[#7A7568] text-xs">In attesa di dati…</p>}
          </div>
        </Card>
      </div>

      {/* Top sezioni */}
      <Card title="🎯 Sezioni più Ingaggianti — tempo reale di lettura" testid="analytics-sections">
        <table className="w-full text-xs text-[#C9C3B5]">
          <thead>
            <tr className="text-[#7A7568] text-left">
              <th className="py-2 font-normal">Sezione</th>
              <th className="font-normal">Visualizzazioni</th>
              <th className="font-normal">Tempo Totale</th>
              <th className="font-normal">Tempo Medio</th>
            </tr>
          </thead>
          <tbody>
            {(data?.top_sections || []).map((s) => (
              <tr key={s.id} className="border-t border-[#222]">
                <td className="py-2 text-[#F2EFE8]">{SECTION_LABELS[s.id] || s.id}</td>
                <td>{s.views}</td>
                <td>{fmtDur(s.total_seconds)}</td>
                <td className="text-[#C4A671]">{fmtDur(s.avg_seconds)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(data?.top_sections || []).length === 0 && <p className="text-[#7A7568] text-xs mt-2">In attesa di dati di scroll…</p>}
      </Card>

      {/* Scansioni QR Collarini per cuvée */}
      <Card title="📌 Scansioni QR Collarini — per Cuvée" testid="analytics-qr">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="text-center py-3 border border-[#C4A671]/40" data-testid="qr-total">
            <div className="font-serif-display italic text-3xl text-[#C4A671]">{data?.qr_scans?.total ?? "—"}</div>
            <div className="overline text-[9px] text-[#8B7A55] mt-2">Totale Scansioni</div>
          </div>
          {["scilla", "terra-arsa", "blanche", "claire"].map((w) => {
            const found = (data?.qr_scans?.by_wine || []).find((x) => x.wine === w);
            return (
              <div key={w} className="text-center py-3 border border-[#2a2a2a]" data-testid={`qr-wine-${w}`}>
                <div className="font-serif-display italic text-3xl text-[#F2EFE8]">{found?.scans ?? 0}</div>
                <div className="overline text-[9px] text-[#8B7A55] mt-2 capitalize">{w.replace("-", " ")}</div>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-[#555] mt-3">Ogni inquadratura del QR sul collarino apre il Concierge della cuvée e viene contata una volta per sessione.</p>
      </Card>

      {/* Contatore Chat Concierge — consumo AI reale */}
      <Card title="💬 Chat Concierge — Consumo AI Reale" testid="analytics-concierge">
        <div className="grid grid-cols-3 gap-4">
          {[
            ["Conversazioni", data?.concierge?.conversations ?? "—", "concierge-conversations"],
            ["Messaggi AI (= crediti)", data?.concierge?.ai_messages ?? "—", "concierge-ai-messages"],
            ["Media msg / conversazione", data?.concierge?.avg_per_conversation ?? "—", "concierge-avg"],
          ].map(([label, value, tid]) => (
            <div key={label} className="text-center py-3" data-testid={tid}>
              <div className="font-serif-display italic text-3xl text-[#F2EFE8]">{value}</div>
              <div className="overline text-[9px] text-[#8B7A55] mt-2">{label}</div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[#555] mt-2">Ogni messaggio scritto da un visitatore al Concierge = 1 chiamata AI che consuma crediti. Il messaggio di benvenuto è statico e non consuma.</p>
      </Card>

      {/* Clarity + B2B */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="🔥 Microsoft Clarity — Heatmaps & Session Recordings" testid="analytics-clarity">
          <p className="text-xs text-[#7A7568] leading-relaxed mb-3">
            Crea un progetto gratuito su clarity.microsoft.com, incolla qui il Project ID e salva:
            lo script si attiverà automaticamente sul sito pubblico.
          </p>
          <div className="flex gap-2">
            <input
              value={clarityId}
              onChange={(e) => setClarityId(e.target.value)}
              placeholder="Project ID (es. abc123xyz)"
              className="flex-1 bg-[#0A0A0A] border border-[#2a2a2a] px-3 py-2 text-sm text-[#F2EFE8] outline-none focus:border-[#C4A671]"
              data-testid="clarity-id-input"
            />
            <button
              onClick={saveClarity}
              className="px-4 py-2 bg-[#C4A671] text-[#0A0A0A] text-[10px] uppercase tracking-[0.15em] hover:bg-[#E0C48A]"
              data-testid="clarity-save-btn"
            >
              {claritySaved ? "✓ Salvato" : "Salva"}
            </button>
          </div>
          {clarityId && (
            <div className="flex flex-wrap gap-2 mt-4">
              {[
                ["Dashboard", "dashboard"],
                ["🔥 Heatmaps", "heatmaps"],
                ["🎥 Recordings", "impressions"],
              ].map(([label, path]) => (
                <a
                  key={path}
                  href={`https://clarity.microsoft.com/projects/view/${clarityId}/${path}`}
                  target="_blank" rel="noreferrer"
                  className="px-3 py-2 border border-[#C4A671]/50 text-[#C4A671] text-[10px] uppercase tracking-[0.12em] hover:bg-[#C4A671]/10"
                  data-testid={`clarity-link-${path}`}
                >
                  {label} ↗
                </a>
              ))}
            </div>
          )}
        </Card>

        <Card title="🏢 B2B Visitor Intelligence — reti aziendali rilevate" testid="analytics-b2b">
          <div className="max-h-80 overflow-y-auto space-y-1">
            {b2bBiz.map((o, i) => (
              <div key={i} className="py-2 border-b border-[#222]">
                <div className="flex justify-between text-xs">
                  <span className="text-[#7CB87C]">🏢 {o.org}</span>
                  <span className="text-[#C4A671]">{o.visits} visite</span>
                </div>
                <div className="text-[10px] text-[#7A7568] mt-0.5">
                  {o.city ? `${o.city}, ` : ""}{o.country || ""} {o.hostname ? `· ${o.hostname}` : ""}
                </div>
              </div>
            ))}
            {b2bBiz.length === 0 && (
              <p className="text-[#7A7568] text-xs">Nessuna rete aziendale rilevata nel periodo (gli accessi da reti residenziali/mobili sono esclusi).</p>
            )}
            {b2bIsp.length > 0 && (
              <details className="pt-2">
                <summary className="text-[10px] text-[#7A7568] cursor-pointer">Reti residenziali / ISP ({b2bIsp.length})</summary>
                {b2bIsp.slice(0, 15).map((o, i) => (
                  <div key={i} className="flex justify-between text-[10px] text-[#555] py-1">
                    <span>{o.org}</span><span>{o.visits}</span>
                  </div>
                ))}
              </details>
            )}
          </div>
          <p className="text-[10px] text-[#555] mt-3">GDPR: IP anonimizzati (ultimo ottetto rimosso), nessun cookie di profilazione, sessioni volatili.</p>
        </Card>
      </div>
    </div>
  );
}
